import { useState, useMemo, useCallback, useEffect } from 'react'
import TwoFactorModal from '@/components/common/TwoFactor/TwoFactorModal'
import GuestLoyaltyLookup from './GuestLoyaltyLookup'
import { ReturningGuestData, SciSettings } from '@/api/interface/self-check-in'
import { Business } from '@/api/interface/business'
import { request2FACode, validate2FACode } from '@/api/nextme/two-factor-auth'
import { formatPhoneNumber } from '@/utils/phone-format-utils'
import { getReturningGuestData } from '@/api/nextme/self-check-ins'
import { Location } from '@/api/interface/location'

export interface RepeatGuestLookup {
  businessId?: Business['id']
  locationId?: Location['id']
  canCloseModal?: boolean
  phone?: string
  isModalOpen?: boolean
  setIsModalOpen: (arg: boolean) => void
  isPhoneValid?: () => boolean
  shouldLookupGuest?: boolean
  isGuestLookingUp?: boolean
  shouldDisplayMatchMessage?: boolean
  require2fa?: SciSettings['require2FA']
  isGuestLookupSuccess?: boolean
  setGuestData: (data: any) => void
  handleClose: () => void
  prefillForm: (data: ReturningGuestData) => void
}

const RepeatGuestLookup = ({
  businessId,
  locationId,
  canCloseModal,
  phone,
  isModalOpen,
  setIsModalOpen,
  isPhoneValid,
  shouldLookupGuest,
  isGuestLookingUp,
  isGuestLookupSuccess,
  shouldDisplayMatchMessage,
  require2fa,
  setGuestData,
  handleClose,
  prefillForm,
}: RepeatGuestLookup) => {
  const [requestCodeAttempts, setRequestCodeAttempts] = useState(0)
  const [isLoadingGuestData, setIsLoadingGuestData] = useState(false)
  const [helperText, setHelperText] = useState('')
  const [isError, setIsError] = useState(false)

  // Request a verification code
  const requestCode = useCallback(async () => {
    const status = await request2FACode({ authValue: phone, authType: 'phone', businessId })

    if (status === 204) {
      setRequestCodeAttempts(prev => prev + 1)
      setIsError(false)
      if (requestCodeAttempts >= 2) {
        setIsError(false)
        setHelperText('We\'ve sent a new code to your device.')
      }
    }
  }, [businessId, phone, requestCodeAttempts])

  const getGuestData = useCallback(async () => {
    setIsLoadingGuestData(true)
    const data = await getReturningGuestData({ phone, locationId })

    
    setIsLoadingGuestData(false)
    if (data) {
      setIsError(false)
      return data
    } else {
      // TODO: if this fails, the user should be able to click 
      // the cta again, but only retry the guest data lookup, not the code verification
      setIsError(true)
      setHelperText('Something went wrong, try again')
    }
  }, [phone, locationId, setGuestData, setIsLoadingGuestData, prefillForm])

  // Submit code for verification
  const submitVerifyCode = useCallback(async (code: string, setVerifyCode: (arg: string) => void) => {

    if (phone && isPhoneValid) {
      const verifyStatus = await validate2FACode({ authCode: code, authValue: phone })

      if (verifyStatus === 200) {
        setIsError(false)
        setHelperText('')
        const guestData = await getGuestData()
        setVerifyCode('')
        prefillForm(guestData)
      } else {
        setIsError(true)
        setHelperText('The code you entered is invalid. Please try again.')
      }
      return verifyStatus
    }
    
  }, [phone])

  // Close modal
  const closeModal = useCallback(() => {
    if (canCloseModal) {
      setGuestData(null)
      setRequestCodeAttempts(0)
      setHelperText('')
      setIsError(false)
      handleClose() // close modal and reset form
    }
  }, [canCloseModal])

  return (
    <>
      <GuestLoyaltyLookup
        shouldDisplayMatchMessage={Boolean(shouldDisplayMatchMessage)}
        isGuestLookingUp={isGuestLookingUp}
        isGuestLookupSuccess={isGuestLookupSuccess}
      />

      <TwoFactorModal
        isOpen={isModalOpen}
        title="👋 Welcome Back!"
        helperText={helperText}
        isError={isError}
        codeCharacterCount={6}
        isLoadingGuestData={isLoadingGuestData}
        requestCode={requestCode}
        handleVerify={submitVerifyCode}
        handleClose={closeModal}
      >
        <p>
          We found an existing guest profile for the phone number you entered.
          Please enter the 6 digit code we just sent to{' '}
          <span className="font-bold">{phone && formatPhoneNumber(phone)}</span>.
        </p>
      </TwoFactorModal>
    </>
  )
}

export default RepeatGuestLookup
